import { useCallback, useEffect, useState } from "react";
import { ApiError } from "./api";

// Tiny wrapper for "load this once, expose loading/error/data + refetch".
// Replaces the boilerplate `useState + useEffect + try/catch` in every list
// page. The loader is identified by `deps` (just like useEffect deps).
export function useResource<T>(
  loader: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loader();
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch, setData };
}
